import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  where,
} from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";

import { db } from "integrations/firebase/firestore";

import { membershipCardSchema } from "../dashboard/schemas";
import { directoryAttendeeSchema } from "../event-experience/schemas";
import {
  buildNoteGroups,
  eventsToResolve,
  type DirectoryPerson,
  type EventStatus,
  type NoteGroup,
} from "./notes-model";
import { personNoteSchema, type PersonNote } from "./schemas";

export type MyNotesState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; groups: NoteGroup[] };

/**
 * Every note the user has written, grouped by event, with what is still
 * known about each event and person.
 *
 * Reads, in order: the notes (one query on the user's own subcollection), the
 * user's memberships (the dashboard's collection-group query, P16), then per
 * member event one directory point read per note, and per other event one
 * `public_events` query by event id and at most one `event_slugs` point read.
 * All of them are reads the rules already grant a signed-in member, so a
 * person who withdrew or whose event was deleted still sees their notes, with
 * the names they stored.
 *
 * Read once per `refreshKey`. The page bumps it after an edit, and a live
 * listener would pull a card out from under an editor whose text was just
 * emptied.
 */
export function useMyNotes(uid: string, refreshKey = 0): MyNotesState {
  const [notes, setNotes] = useState<
    | { status: "loading" }
    | { status: "error"; error: string }
    | { status: "success"; notes: PersonNote[] }
  >({ status: "loading" });
  const [statuses, setStatuses] = useState<Map<string, EventStatus>>(new Map());
  const [directory, setDirectory] = useState<
    Map<string, DirectoryPerson | null>
  >(new Map());

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const snapshot = await getDocs(
          collection(db, "users", uid, "person_notes"),
        );
        const parsed: PersonNote[] = [];
        snapshot.forEach((item) => {
          const result = personNoteSchema.safeParse({
            id: item.id,
            ...item.data(),
          });
          if (result.success) parsed.push(result.data);
        });
        if (cancelled) return;
        setNotes({ status: "success", notes: parsed });
        if (parsed.length === 0) return;

        const nextStatuses = new Map<string, EventStatus>();
        const memberships = await getDocs(
          query(collectionGroup(db, "members"), where("uid", "==", uid)),
        ).catch(() => null);
        memberships?.forEach((item) => {
          const card = membershipCardSchema.safeParse(item.data());
          if (!card.success) return;
          nextStatuses.set(card.data.eventId, {
            kind: "member",
            title: card.data.eventTitle,
            slug: card.data.eventSlug ?? undefined,
          });
        });
        const memberIds = new Set(nextStatuses.keys());

        const [others, people] = await Promise.all([
          Promise.all(
            eventsToResolve(parsed, memberIds).map(async (eventId) => {
              const slug = parsed.find(
                (note) => note.eventId === eventId && note.eventSlug,
              )?.eventSlug;
              return [eventId, await resolveEvent(eventId, slug)] as const;
            }),
          ),
          Promise.all(
            parsed
              .filter((note) => memberIds.has(note.eventId))
              .map(
                async (note) => [note.id, await readDirectory(note)] as const,
              ),
          ),
        ]);
        if (cancelled) return;
        for (const [eventId, status] of others) {
          nextStatuses.set(eventId, status);
        }
        const nextDirectory = new Map<string, DirectoryPerson | null>();
        for (const [noteId, person] of people) {
          if (person !== undefined) nextDirectory.set(noteId, person);
        }
        setStatuses(nextStatuses);
        setDirectory(nextDirectory);
      } catch (error) {
        if (cancelled) return;
        setNotes({
          status: "error",
          error:
            error instanceof Error
              ? error.message
              : "Could not load your notes",
        });
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [uid, refreshKey]);

  return useMemo(() => {
    if (notes.status !== "success") return notes;
    return {
      status: "success",
      groups: buildNoteGroups(notes.notes, statuses, directory),
    };
  }, [notes, statuses, directory]);
}

/**
 * The directory entry behind a note: the entry, `null` when the directory
 * answered that the person is not listed, `undefined` when it could not be
 * asked (a failed read says nothing about the person).
 */
async function readDirectory(
  note: PersonNote,
): Promise<DirectoryPerson | null | undefined> {
  try {
    const snapshot = await getDoc(
      doc(db, "events", note.eventId, "directory", note.attendeeId),
    );
    if (!snapshot.exists()) return null;
    const entry = directoryAttendeeSchema.safeParse({
      id: snapshot.id,
      ...snapshot.data(),
    });
    return entry.success
      ? { name: entry.data.name, avatar: entry.data.avatar }
      : undefined;
  } catch {
    return undefined;
  }
}

/**
 * An event the author is no longer a member of. A publication found by event
 * id (whatever its current address) means it is live; an address reservation
 * under the stored slug that still names this event means it exists
 * unpublished; neither means it was deleted, because deleting an event is
 * what removes both.
 */
async function resolveEvent(
  eventId: string,
  storedSlug: string | undefined,
): Promise<EventStatus> {
  try {
    const published = await getDocs(
      query(
        collection(db, "public_events"),
        where("eventId", "==", eventId),
        limit(1),
      ),
    );
    const found = published.docs[0];
    if (found) {
      const data = found.data();
      return {
        kind: "not-registered",
        title: typeof data.title === "string" ? data.title : undefined,
        slug: found.id,
      };
    }
    if (storedSlug) {
      const reservation = await getDoc(doc(db, "event_slugs", storedSlug));
      if (reservation.exists() && reservation.get("eventId") === eventId) {
        return { kind: "unpublished" };
      }
    }
    return { kind: "deleted" };
  } catch {
    return { kind: "checking" };
  }
}
